/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { PersonPinCircleOutlined, CalendarToday } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Typography,
  Stack,
  Box
} from "@mui/material";
import { AppointmentInterface, VisitStatusEnum } from "@malmur-technologies/medicalsystem-shared";
import moment from "moment";
import { useTranslation } from "react-i18next";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { FC } from "react";
import { useMaterialUIController } from "context/ThemeContext";

/**
 * Props for AppointmentCard component.
 * @interface AppointmentCardProps
 * @property {AppointmentInterface} appointment - The appointment data to display
 * @property {() => void} [onClick] - Optional click handler for the card
 * @property {string} [color] - Optional custom card background color
 * @property {boolean} [showDate] - Optional flag to show the full date at the top of the card
 */
interface AppointmentCardProps {
  appointment: AppointmentInterface;
  onClick?: () => void;
  color?: string;
  showDate?: boolean;
}

/**
 * Card component displaying appointment information with status-based coloring.
 * 
 * Features:
 * - Color-coded by visit status (Pending, Check-in, Check-out, Cancelled)
 * - Dark mode support
 * - Past due indicator for overdue check-ins
 * - Patient and provider information display
 * - Appointment date/time with formatting
 * - Chief complaint display
 * - Status chip with localized text
 * - Clickable action area
 * 
 * Status Colors:
 * - Cancelled: Red tones
 * - Past Due Check-in: Orange/yellow tones
 * - Checked Out: Green tones
 * - Pending/Scheduled: Blue tones
 * 
 * @param appointment - Appointment data including patient, provider, time, status
 * @param onClick - Handler when card is clicked
 * @param color - Override default status-based coloring
 * @returns Styled appointment card with status indicators
 */
const AppointmentCard: FC<AppointmentCardProps> = ({ appointment, onClick, color, showDate = false }) => {
  const { t, i18n } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const getCardColor = (): string => {
    const now = moment();
    const appointmentTime = moment(appointment.arrivalDateTime);
    const appointmentEndTime = appointmentTime.clone().add(appointment.duration || 0, 'minutes');
    const isPastDue = appointmentEndTime.isBefore(now);

    let finalColor = "";

    if (appointment.visitStatus === VisitStatusEnum.CANCEL) {
      finalColor = darkMode ? "card-color-cancel-dark" : "card-color-cancel-light";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_IN && isPastDue) {
      finalColor = darkMode ? "card-color-checkIn-dark" : "card-color-checkIn-light";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_OUT) {
      finalColor = darkMode ? "card-color-checkOut-dark" : "card-color-checkOut-light";
    } else {
      finalColor = darkMode ? "card-background-color-dark" : "card-background-color-light";
    }

    return finalColor;
  };

  const getChipColor = (): string => {
    const now = moment();
    const appointmentTime = moment(appointment.arrivalDateTime);
    const appointmentEndTime = appointmentTime.clone().add(appointment.duration || 0, 'minutes');
    const isPastDue = appointmentEndTime.isBefore(now);

    let chipColor = "";

    if (appointment.visitStatus === VisitStatusEnum.CANCEL) {
      chipColor = darkMode ? "cancel-light" : "cancel-dark";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_IN && isPastDue) {
      chipColor = darkMode ? "checkIn-light" : "checkIn-dark";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_OUT) {
      chipColor = darkMode ? "checkOut-light" : "checkOut-dark";
    } else {
      chipColor = darkMode ? "card-color-light" : "card-color-dark";
    }

    return chipColor;
  };

  const getTextColor = (): string => {
    return darkMode ? "dark-color" : "light-color";
  };

  const getStatusText = (status: VisitStatusEnum) => {
    switch (status) {
      case VisitStatusEnum.PENDING:
        return t("appointmentStatus.scheduled");
      case VisitStatusEnum.CHECK_IN:
        return t("appointmentStatus.arrive");
      case VisitStatusEnum.LAB:
        return t("appointmentStatus.inProgress");
      case VisitStatusEnum.CHECK_OUT:
        return t("appointmentStatus.checkedOut");
      case VisitStatusEnum.CANCEL:
        return t("appointmentStatus.cancelled");
      case VisitStatusEnum.NO_SHOW:
        return t("appointmentStatus.noShow");
      default:
        return status;
    }
  };

  const patientName = appointment.patient?.person
    ? `P. ${appointment.patient.person.name} ${appointment.patient.person.lastname}`
    : t("appointmentCard.noPatient");

  const providerName = appointment.provider?.person
    ? `Dr. ${appointment.provider.person.name} ${appointment.provider.person.lastname}`
    : t("appointmentCard.noProvider");

  const appointmentDate = moment(appointment.arrivalDateTime).locale(i18n.language).format("dddd, MMMM D, YYYY");
  const appointmentTime = moment(appointment.arrivalDateTime).format("HH:mm");
  const endTime = moment(appointment.arrivalDateTime).add(appointment.duration, "minutes").format("HH:mm");
  const cardColor = getCardColor();
  const chipColor = getChipColor();
  const textColor = getTextColor();

  const renderCardContent = () => (
    <CardContent className="card-content">
      <Box>
        {showDate && (
          <Stack className={`card-header ${darkMode ? "dark-card-header" : "light-card-header"}`}>
            <CalendarToday className={`card-icon-text ${textColor}`} />
            <Typography
              variant="subtitle2"
              className={`subtitle2 ${textColor}`}
            >
              {appointmentDate}
            </Typography>
          </Stack>
        )}
        <Stack className="card-row-space">
          <Typography variant="h6" className={`card-title ${textColor}`}>
            {appointment.reason || t("appointmentCard.noReason")}
          </Typography>
          <Chip
            label={getStatusText(appointment.visitStatus)}
            size="small"
            className={`chip-light ${chipColor}`}
          />
        </Stack>
        <Stack className="card-stack-row-center">
          <PersonPinCircleOutlined className={`card-icon-text ${textColor}`} />
          <Typography variant="body2" className={`card-text-no-wrap ${textColor}`}>
            {patientName}
          </Typography>
        </Stack>
        <Stack className="card-stack-row-center">
          <MedicalServicesIcon className={`card-icon-text ${textColor}`} />
          <Typography variant="body2" className={`card-text-no-wrap ${textColor}`}>
            {providerName}
          </Typography>
        </Stack>
      </Box>
      <Stack className="stack-row-space-between">
        <Stack className="card-stack-row-center">
          <AccessTimeIcon className={`card-icon-text ${textColor}`} />
          <Typography variant="body2" className={`${textColor}`}>
            {appointmentTime} - {endTime}
          </Typography>
        </Stack>
        <Typography variant="caption" className={`${textColor}`}>
          {appointment.duration} min
        </Typography>
      </Stack>
    </CardContent>
  );
  return (
    <Card className={`appointment-card ${cardColor}`}>
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
          {renderCardContent()}
        </CardActionArea>
      ) : (
        <Box sx={{ height: "100%" }}>
          {renderCardContent()}
        </Box>
      )}
    </Card>
  );
};

export default AppointmentCard;